import React, { useEffect, useRef } from "react";
import "./RelatorioExtracoesPrint.css";
import RelatorioCalibracaoPrensaPrint from "../CalibracaoPrensa/RelatorioCalibracaoPrensaPrint";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function formatarDataBR(dataISO) {
  if (!dataISO) return "";
  const [ano, mes, dia] = dataISO.split("-");
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

function formatarDataExtenso(dataISO) {
  if (!dataISO) return "XX de XXXXXXXX de 2025";

  const meses = [
    "janeiro",
    "fevereiro",
    "marco",
    "abril",
    "maio",
    "junho",
    "julho",
    "agosto",
    "setembro",
    "outubro",
    "novembro",
    "dezembro",
  ];

  const [ano, mes, dia] = dataISO.split("-");
  return `${Number(dia)} de ${meses[Number(mes) - 1]}, ${ano}`;
}

function numeroBR(valor, casas = 2) {
  if (valor === "" || valor === null || valor === undefined) return "";
  if (String(valor).trim() === "-") return "-";
  const valorNumerico = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(valorNumerico)) return "";
  return valorNumerico.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return 0;
  return Number(String(valor).replace(",", "."));
}

function temSinalAusencia(...valores) {
  return valores.some((valor) => String(valor ?? "").trim() === "-");
}

function valorCalculadoOuTraco(valor, ...dependencias) {
  if (valor !== "" && valor !== null && valor !== undefined) return valor;
  return temSinalAusencia(...dependencias) ? "-" : "";
}

function coeficientesPreenchidos(item = {}) {
  return [item.k1, item.k2, item.k3, item.k4].every(
    (coeficiente) => String(coeficiente ?? "").trim() !== ""
  );
}

function calcularTensaoInicial(item) {
  if (temSinalAusencia(item.cargaRupturaTf, item.diametro)) return "-";

  const carga = numero(item.cargaRupturaTf);
  const diametro = numero(item.diametro);
  if (!carga || !diametro) return "";

  const diametroArea = Math.round(diametro);
  const area = (3.1416 * Math.pow(diametroArea, 2)) / 4;
  return ((carga * 1000 * 9.80665) / area).toFixed(2);
}

function calcularTensaoCorrigida(item) {
  const inicial = numero(item.tensaoInicial || calcularTensaoInicial(item));
  const k1 = valorCalculadoOuTraco(item.k1, item.altura, item.diametro);
  const k2 = valorCalculadoOuTraco(item.k2, item.diametro);

  if (temSinalAusencia(item.tensaoInicial || calcularTensaoInicial(item), k1, k2, item.k3, item.k4)) {
    return "-";
  }

  if (!coeficientesPreenchidos({ k1, k2, k3: item.k3, k4: item.k4 })) return "";
  if (!inicial) return "";

  const somaCoeficientes =
    numero(k1) +
    numero(k2) +
    numero(item.k3) +
    numero(item.k4);

  return (inicial * (1 + somaCoeficientes)).toFixed(1);
}

function obterTensaoCorrigida(item) {
  return item.tensaoCorrigida || calcularTensaoCorrigida(item);
}

function calcularForcaRuptura(item) {
  if (temSinalAusencia(item.cargaRupturaTf)) return "-";
  const carga = numero(item.cargaRupturaTf);
  return carga ? carga * 9806.65 : "";
}

function calcularMassaEspecifica(item) {
  if (temSinalAusencia(item.massa, item.diametro, item.altura)) return "-";

  const massa = numero(item.massa);
  const diametro = numero(item.diametro);
  const altura = numero(item.altura);

  if (!massa || !diametro || !altura) return "";

  const volumeCm3 = Math.PI * Math.pow(diametro / 20, 2) * (altura / 10);
  return (massa / volumeCm3).toFixed(3);
}

function linhasCompletas(testemunhos = []) {
  return Array.isArray(testemunhos) ? testemunhos : [];
}

function imprimirEmRetrato() {
  const styleId = "relatorio-extracoes-print-orientacao";
  document.getElementById(styleId)?.remove();

  const style = document.createElement("style");
  style.id = styleId;
  style.media = "print";
  style.textContent = `
    @page {
      size: A4 portrait;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
  window.print();
}

function dividirEmPaginas(lista, quantidade) {
  const paginas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    paginas.push(lista.slice(i, i + quantidade));
  }
  return paginas;
}

function dividirEmLinhas(lista, quantidade) {
  const linhas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    linhas.push(lista.slice(i, i + quantidade));
  }
  return linhas;
}

function CabecalhoRelatorio({ extracao, dataRelatorio, anoRelatorio }) {
  return (
    <>
      <table className="cabecalho">
        <tbody>
          <tr>
            <td rowSpan="3" style={{ width: 130 }}>
              <LogoRelatorio alt="Logo" className="logo"  />
            </td>
            <td rowSpan="3" className="titulo-principal">
              SISTEMA DE GESTÃO DA QUALIDADE
              <br />
              Relatório de ensaio - Extração, preparo e análise de testemunho
              de
              <br />
              estrutura de concreto
            </td>
            <td style={{ width: 95 }}>RQ 2.2.14</td>
          </tr>
          <tr>
            <td>Revisao 00</td>
          </tr>
          <tr>
            <td>20/08/2025</td>
          </tr>
        </tbody>
      </table>

      <div className="linha-info">
        <span>
          <strong>DATA:</strong> {formatarDataBR(dataRelatorio)}
        </span>
        <span>
          <strong>CODIGO:</strong>{" "}
          {extracao.codigo || `CE-RE-ET-000/${anoRelatorio}`}
        </span>
      </div>

      <div className="dados-obra">
        <div className="dados-obra-linha">
          <strong className="dados-obra-label">EMPRESA/CONTRATANTE:</strong>
          <span>
            {extracao.cliente || "-"}
            {extracao.clienteDocumento ? ` - CNPJ: ${extracao.clienteDocumento}` : ""}
          </span>
        </div>
        <div className="dados-obra-linha">
          <strong className="dados-obra-label">EMPREENDIMENTO:</strong>
          <span>{extracao.obra || "-"}</span>
        </div>
        <div className="dados-obra-linha">
          <strong className="dados-obra-label">LOCAL:</strong>
          <span>{extracao.local || extracao.endereco || "-"}</span>
        </div>
      </div>
    </>
  );
}

function RelatorioFotografico({ extracao, fotos = [], dataRelatorio, anoRelatorio }) {
  if (!fotos.length) return null;

  return dividirEmPaginas(fotos, 6).map((paginaFotos, paginaIndex) => (
    <div className="pagina-fotografica" key={`pagina-fotos-${paginaIndex}`}>
      <CabecalhoRelatorio
        extracao={extracao}
        dataRelatorio={dataRelatorio}
        anoRelatorio={anoRelatorio}
      />
      <div className="faixa-relatorio-fotografico">RELATÓRIO FOTOGRÁFICO</div>

      <div className="grade-fotografica">
        {dividirEmLinhas(paginaFotos, 2).map((linhaFotos, linhaIndex) => (
          <div className="linha-fotografica-bloco" key={`linha-fotos-${paginaIndex}-${linhaIndex}`}>
            <div className="linha-fotografica">
              {linhaFotos.map((foto, index) => {
                const fotoNumero = paginaIndex * 6 + linhaIndex * 2 + index + 1;

                return (
                  <div className="foto-relatorio-item" key={foto.id || foto.url || `${paginaIndex}-${fotoNumero}`}>
                    <div className="foto-relatorio-imagem">
                      <img src={foto.url} alt={foto.legenda || `Foto ${fotoNumero}`} />
                    </div>
                    <div className="foto-relatorio-legenda">
                      {foto.legenda || `FOTO ${String(fotoNumero).padStart(2, "0")}`}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="linha-azul-fotos" aria-hidden="true" />
          </div>
        ))}
      </div>
      <RodapeRestrito />
    </div>
  ));
}

function RodapeRestrito({ className = "" }) {
  return (
    <div className={`rodape-restrito ${className}`.trim()}>
      DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA EMPRESA
    </div>
  );
}

export default function RelatorioExtracoesPrint({
  extracao,
  fotosRelatorio = [],
  onVoltar,
  autoPrint = false,
}) {
  const jaImprimiuRef = useRef(false);
  useNomeArquivoPdf(extracao?.codigo);
  const testemunhos = linhasCompletas(extracao?.testemunhos);
  const quantidadeCpsEnsaiados =
    Number(extracao?.quantidadeTestemunhos) || testemunhos.length;
  const quantidadeCpsRompidos = testemunhos.filter(
    (item) => numero(item.cargaRupturaTf) > 0
  ).length;
  const datasExtracao = [
    extracao?.dataExtracao,
    ...testemunhos.map((item) => item?.dataExtracao),
  ].filter((data) => data && data !== "-");
  const datasExtracaoUnicas = [...new Set(datasExtracao)];
  const possuiDatasExtracaoDiversas = datasExtracaoUnicas.length > 1;
  const dataExtracaoRelatorio =
    extracao?.dataExtracao || datasExtracaoUnicas[0] || "";
  const fotosAnexo = fotosRelatorio.length
    ? fotosRelatorio
    : Array.isArray(extracao?.relatorioFotografico)
    ? extracao.relatorioFotografico.filter((foto) => foto?.url)
    : [];
  const dataRelatorio =
    extracao?.dataRelatorio || new Date().toISOString().split("T")[0];
  const anoRelatorio = (dataRelatorio || "").split("-")[0] || "2025";
  const empreendimentoLocal = [extracao?.obra, extracao?.local || extracao?.endereco]
    .filter(Boolean)
    .join(" - ");

  useEffect(() => {
    if (!autoPrint || jaImprimiuRef.current) return;

    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(imprimirEmRetrato);

    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint]);

  if (!extracao) return null;

  return (
    <div className="relatorio-extracoes-print">
      {onVoltar && (
        <div className="acoes-print">
          <button className="btn-voltar" onClick={onVoltar}>
            Voltar
          </button>
          <button className="btn-imprimir" onClick={imprimirEmRetrato}>
            Imprimir
          </button>
        </div>
      )}

      <div className="pagina">
        <table className="relatorio-principal-tabela">
          <thead>
            <tr className="cabecalho-margem-topo">
              <td aria-hidden="true"></td>
            </tr>
            <tr>
              <td>
                <CabecalhoRelatorio
                  extracao={extracao}
                  dataRelatorio={dataRelatorio}
                  anoRelatorio={anoRelatorio}
                />
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>

        <div className="faixa-azul">RELATÓRIO DE ENSAIO</div>

        <div className="secao">1. OBJETIVO.</div>
        <div className="texto">
          Este relatório tem por objetivo apresentar os resultados da análise dos
          testemunhos extraídos das estruturas de concreto da obra{" "}
          {empreendimentoLocal || "-"}.
        </div>

        <div className="secao">2. METODOLOGIA.</div>
        <div className="texto">
          <strong>Os ensaios seguiram as prescrições das normas da ABNT:</strong>
          <br />
          NBR 7680-1 - Concreto - Extração, preparo, ensaio e análise Parte 1:
          Resistência à compressão axial de testemunhos de estruturas de
          concreto;
          <br />
          NBR 5739 - Concreto - Ensaio de compressão de corpos de prova
          cilíndricos;
          <br />
          NBR 8953 - Concreto para fins estruturais - Classificação pela massa
          específica, por grupos de resistência e consistência;
          <br />
          NBR 6118 - Projeto de estruturas de concreto - Procedimento.
        </div>

        <div className="secao">3. ENSAIOS EM ESTRUTURAS DE CONCRETO.</div>
        <div className="texto">
          * Extração, preparo, ensaio e análise de resistência à compressão
          axial de testemunhos de estruturas de concreto.
    
        </div>

        <div className="subsecao">
          3.1. Análise dos testemunhos - resistência à compressão axial
        </div>
        <table className="tabela-analise">
          <thead>
            <tr>
              <th rowSpan="2">Nº CP</th>
              <th colSpan="2">Dimensão dos CP's</th>
              <th rowSpan="2">Carga de Ruptura (Tf)</th>
              <th rowSpan="2">Força de Ruptura (N)</th>
              <th rowSpan="2">Tensão fci,ext inicial (MPa)</th>
              <th colSpan="4">Coefic. de correção</th>
              <th rowSpan="2">Tensão fci,ext (MPa)</th>
              <th rowSpan="2">Massa Específica (g/m3)</th>
              <th rowSpan="2">Frente de carbonatação</th>
            </tr>
            <tr>
              <th>Diâm. (mm)</th>
              <th>Altura (mm)</th>
              <th>K1</th>
              <th>K2</th>
              <th>K3</th>
              <th>K4</th>
            </tr>
          </thead>
          <tbody>
            {testemunhos.map((item, index) => {
              const tensaoInicial = item.tensaoInicial || calcularTensaoInicial(item);
              const tensaoCorrigida = obterTensaoCorrigida(item);
              const massaEspecifica =
                item.massaEspecifica || calcularMassaEspecifica(item);
              const k1 = valorCalculadoOuTraco(item.k1, item.altura, item.diametro);
              const k2 = valorCalculadoOuTraco(item.k2, item.diametro);

              return (
                <tr key={index}>
                  <td>{item.peca || index + 1}</td>
                  <td>{item.diametro || ""}</td>
                  <td>{item.altura || ""}</td>
                  <td>{numeroBR(item.cargaRupturaTf)}</td>
                  <td>
                    {numeroBR(item.forcaRupturaN || calcularForcaRuptura(item), 0)}
                  </td>
                  <td>{numeroBR(tensaoInicial, 1)}</td>
                  <td>{numeroBR(k1)}</td>
                  <td>{numeroBR(k2)}</td>
                  <td>{numeroBR(item.k3)}</td>
                  <td>{numeroBR(item.k4)}</td>
                  <td className="tensao-corrigida-valor" style={{ color: "#000", fontWeight: 700 }}>{tensaoCorrigida ? numeroBR(tensaoCorrigida, 1) : "-"}</td>
                  <td>{numeroBR(massaEspecifica, 3)}</td>
                  <td>{item.frenteCarbonatacao || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="formula">
          Fci, ext. (MPa) = (CR * AG / ACP) * (1+k1+k2+k3+k4)
        </div>
        <div className="legendas">
          <div>
            <strong>LEGENDAS:</strong>
            <br />
            CR = Carga de Ruptura (kgf);
            <br />
            AG = Aceleração da Gravidade m/s2 (9,80665);
            <br />
            ACP = Área do corpo de prova (mm2);
            <br />
            k1 = Coeficiente de Correção (Relação h/d);
          </div>
          <div>
            k2 = Coeficiente de Correção (Efeito Broqueamento);
            <br />
            k3 = Coeficiente de Correção (Direção da Extração);
            <br />
            k4 = Coeficiente de Correção (Efeito Umidade);
            <br />
            CP = Corpo de prova.
          </div>
        </div>

        <div className="subsecao">3.1.1. Observações.</div>
        <div className="texto">
          {possuiDatasExtracaoDiversas
            ? "As extrações dos testemunhos foram realizadas em datas diversas."
            : `As extrações dos testemunhos foram realizadas no dia ${formatarDataExtenso(dataExtracaoRelatorio)}.`}
          <br />
          A atividade de extrações dos testemunhos foram acompanhadas por
          representantes da empresa. Os corpos de prova foram rompidos{" "}
          {extracao.horasAposExtracao || "XX"} h após a extração.
          <br />
          Os testemunhos foram mantidos expostos ao ar, em ambiente de laboratório
          até a data dos ensaios de compressão, pelo fato do concreto da região da
          estrutura examinada não estar em contato com água após extrações. k4 =
          {extracao.k4Observacao || " -0,04"}.
          <br />
          Os CPs foram retificados e capeados com argamassa de alta resistência.
          (A verificação das dimensões e massa dos testemunhos foram realizadas
          antes do capeamento).
          <br />
          Total de corpos de prova analisados: {quantidadeCpsEnsaiados} CP(s) e
          total de corpos de prova rompidos: {quantidadeCpsRompidos} CP(s).
          {extracao.observacoes ? (
            <>
              <br />
              {extracao.observacoes}
            </>
          ) : null}
        </div>

        <div className="bloco-resumo-resultados">
          <div className="subsecao">
            3.2. Quadro resumo, identificação das peças e valores obtidos.
          </div>
          <div className="resultado-titulo">RESULTADOS OBTIDOS</div>
          <table className="tabela-resumo">
            <thead>
              <tr>
                <th>Nº CP</th>
                <th>Data da extração</th>
                <th>Identificação do CP</th>
                <th>Posição/Face</th>
                <th>CARGA (Tf)</th>
                <th>TENSÃO fci,ext MÁXIMA (MPa)</th>
                <th>OBSERVAÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {testemunhos.map((item, index) => (
                <tr key={index}>
                  <td>{item.peca || index + 1}</td>
                  <td>{formatarDataBR(item.dataExtracao || extracao.dataExtracao)}</td>
                  <td>{item.identificacao || ""}</td>
                  <td>{item.posicaoFace || "-"}</td>
                  <td>{numeroBR(item.cargaRupturaTf)}</td>
                  <td className="tensao-corrigida-valor" style={{ color: "#000", fontWeight: 700 }}>{obterTensaoCorrigida(item) ? numeroBR(obterTensaoCorrigida(item), 1) : "-"}</td>
                  <td>{item.observacao || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="subsecao">3.3. Resultado/Aceitação.</div>
        <div className="texto">
          Para efeitos de aceitação do concreto, nos casos de não conformidade
          com os critérios da ABNT NBR 12655, deve ser considerado para
          comparação com fck o maior valor de resistência dos testemunhos
          extraídos de cada lote, corrigidos pelos coeficientes estabelecidos na
          NBR 7680.
        </div>

        <div className="secao">4. ANEXOS.</div>
        <div className="texto">
          Anexo 1 - Relatório fotográfico dos testemunhos extraídos;
          {!extracao.prensaExternaSemCertificado && (
            <>
              <br />
              Anexo 2 - Certificado de calibração da Prensa de ensaio de CP;
            </>
          )}
        </div>

        <div className="secao">5. CONSIDERAÇÕES FINAIS.</div>
        <div className="conformidade">
         
        </div>
        {extracao.prensaExternaSemCertificado && (
          <p className="texto">
            Prensa utilizada no ensaio: {extracao.identificacaoPrensaExterna || "-"}.
          </p>
        )}
        <p className="texto">
          Os resultados de ensaios e as considerações feitas neste relatório têm
          significado restrito aos locais informados, às amostras analisadas e às
          condições em que os ensaios foram executados.
        </p>

        <div className="assinatura">
          <img src="/Images/assinaturaLeal.png" alt="Assinatura" />
          <div className="assinatura-nome">Jose Carlos Lemes Leal</div>
          <div>Eng. Civil Esp. CREA 203379/D - TO</div>
        </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <RodapeRestrito />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <RelatorioFotografico
        extracao={extracao}
        fotos={fotosAnexo}
        dataRelatorio={dataRelatorio}
        anoRelatorio={anoRelatorio}
      />

      {!extracao.prensaExternaSemCertificado && extracao.prensaRuptura && (
        <div className="certificado-prensa-anexo">
          <RelatorioCalibracaoPrensaPrint dados={extracao.prensaRuptura} />
        </div>
      )}
    </div>
  );
}
